import { createContext, useContext, useMemo } from 'react';
import { useApp } from '../../../context/AppContext';
import { PROGRAM_MAPPING } from '../../../utils/dataProcessor/constants.js';
import { parseCountryList } from '../../../utils/dataProcessor/parsers';

/**
 * PortfolioDataContext
 * Shared state for all Portfolio (cross-program) charts and components.
 * Aggregates data across ALL programs — not filtered to a single initiative.
 */
const PortfolioDataContext = createContext(null);

// SP01–SP13 canonical program codes (display order)
const SP_CODES = [
  'SP01', 'SP02', 'SP03', 'SP04', 'SP05', 'SP06',
  'SP07', 'SP08', 'SP09', 'SP10', 'SP11', 'SP12', 'SP13',
];

// Impact area fields and their display labels
const IMPACT_AREA_FIELDS = [
  { field: 'genderLevel',       label: 'Gender' },
  { field: 'climateLevel',      label: 'Climate' },
  { field: 'nutritionLevel',    label: 'Nutrition' },
  { field: 'biodiversityLevel', label: 'Biodiversity' },
  { field: 'povertyLevel',      label: 'Poverty' },
];

// Macro-region grouping: maps subregion strings (lowercased) to a bucket label
const REGION_BUCKETS = {
  Africa: [
    'western africa', 'eastern africa', 'southern africa',
    'central africa', 'northern africa', 'sub-saharan africa', 'africa',
  ],
  Asia: [
    'southern asia', 'south-eastern asia', 'eastern asia', 'central asia',
    'western asia', 'south asia', 'southeast asia', 'east asia', 'asia',
  ],
  Americas: [
    'latin america and the caribbean', 'south america', 'central america',
    'northern america', 'caribbean', 'americas',
  ],
};

/**
 * PortfolioDataProvider
 * Wrap portfolio section content in this provider to share computed data across
 * all portfolio charts without prop threading.
 */
export function PortfolioDataProvider({ children }) {
  const { filteredResults, stats } = useApp();

  // ------------------------------------------------------------------
  // Results per program (SP01–SP13)
  // ------------------------------------------------------------------
  const programBreakdown = useMemo(() => {
    const counts = {};
    SP_CODES.forEach(code => { counts[code] = 0; });

    filteredResults.forEach(r => {
      const code = r.initiative?.match(/^(SP\d+|INIT-\d+)/)?.[1];
      // Normalise legacy INIT codes → SP codes via PROGRAM_MAPPING display name
      if (!code) return;
      if (counts[code] !== undefined) {
        counts[code]++;
      } else {
        // Try to resolve via PROGRAM_MAPPING display name (legacy INIT codes)
        const displayName = PROGRAM_MAPPING[code];
        if (displayName) {
          const spCode = displayName.match(/^(SP\d+)/)?.[1];
          if (spCode && counts[spCode] !== undefined) {
            counts[spCode]++;
          }
        }
      }
    });

    return SP_CODES.map(code => ({
      program: code,
      name: PROGRAM_MAPPING[code] || code,
      shortName: code,
      count: counts[code] || 0,
    })).sort((a, b) => b.count - a.count);
  }, [filteredResults]);

  // ------------------------------------------------------------------
  // Results by impact area (Principal + Significant level tags)
  // ------------------------------------------------------------------
  const impactAreaBreakdown = useMemo(() => {
    const counts = {};
    IMPACT_AREA_FIELDS.forEach(({ label }) => { counts[label] = { principal: 0, significant: 0, total: 0 }; });

    filteredResults.forEach(r => {
      IMPACT_AREA_FIELDS.forEach(({ field, label }) => {
        const val = r[field];
        if (val === 'Principal' || val === '(2) Principal') {
          counts[label].principal++;
          counts[label].total++;
        } else if (val === 'Significant' || val === '(1) Significant') {
          counts[label].significant++;
          counts[label].total++;
        }
      });
    });

    return IMPACT_AREA_FIELDS.map(({ label }) => ({
      name: label,
      Principal: counts[label].principal,
      Significant: counts[label].significant,
      total: counts[label].total,
    }));
  }, [filteredResults]);

  // ------------------------------------------------------------------
  // Results by macro-region bucket (Africa / Asia / Americas + Other)
  // ------------------------------------------------------------------
  const regionalBreakdown = useMemo(() => {
    const bucketCounts = { Africa: 0, Asia: 0, Americas: 0, Other: 0 };

    filteredResults.forEach(r => {
      const regions = r.regionsParsed
        || (r.regions ? parseCountryList(r.regions) : []);

      if (regions.length === 0) {
        bucketCounts.Other++;
        return;
      }

      let matched = false;
      regions.forEach(reg => {
        const lower = reg.toLowerCase();
        for (const [bucket, keywords] of Object.entries(REGION_BUCKETS)) {
          if (keywords.some(kw => lower.includes(kw) || kw.includes(lower))) {
            bucketCounts[bucket]++;
            matched = true;
            break;
          }
        }
        if (!matched) bucketCounts.Other++;
      });
    });

    return Object.entries(bucketCounts)
      .map(([name, count]) => ({ name, count }))
      .filter(d => d.count > 0)
      .sort((a, b) => b.count - a.count);
  }, [filteredResults]);

  // ------------------------------------------------------------------
  // Results by type
  // ------------------------------------------------------------------
  const typeBreakdown = useMemo(() => {
    const counts = {};
    filteredResults.forEach(r => {
      if (!r.type) return;
      counts[r.type] = (counts[r.type] || 0) + 1;
    });
    return Object.entries(counts)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);
  }, [filteredResults]);

  // ------------------------------------------------------------------
  // Funding breakdown: W1W2 vs W3Bilateral
  // ------------------------------------------------------------------
  const fundingBreakdown = useMemo(() => {
    let w1w2 = 0;
    let w3Bilateral = 0;
    filteredResults.forEach(r => {
      if (r.fundingType === 'W3Bilateral') w3Bilateral++;
      else w1w2++;
    });
    return [
      { name: 'W1/W2 Pooled', count: w1w2, key: 'w1w2' },
      { name: 'W3/Bilateral', count: w3Bilateral, key: 'w3bilateral' },
    ];
  }, [filteredResults]);

  // ------------------------------------------------------------------
  // Cross-program results: results attributed to multiple Contributing Initiatives
  // ------------------------------------------------------------------
  const crossProgramResults = useMemo(() => {
    return filteredResults.filter(r => {
      // contributingInitiatives is parsed as an array by parseInitiatives();
      // fall back to splitting a raw string if the field happens to be a string.
      const rawCI = r.contributingInitiatives || r.contributing_initiatives;
      if (!rawCI) return false;
      const codes = Array.isArray(rawCI)
        ? rawCI.filter(Boolean)
        : String(rawCI).split(/[,;]/).map(s => s.trim()).filter(Boolean);
      return codes.length > 1;
    });
  }, [filteredResults]);

  // ------------------------------------------------------------------
  // Top countries by result count
  // ------------------------------------------------------------------
  const countryBreakdown = useMemo(() => {
    const counts = {};
    filteredResults.forEach(r => {
      const countries = r.countriesParsed
        || (r.countries ? parseCountryList(r.countries) : []);
      countries.forEach(c => {
        if (c) counts[c] = (counts[c] || 0) + 1;
      });
    });
    return Object.entries(counts)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 20);
  }, [filteredResults]);

  // ------------------------------------------------------------------
  // Partner type breakdown
  // ------------------------------------------------------------------
  const partnerBreakdown = useMemo(() => {
    const counts = {};
    filteredResults.forEach(r => {
      const partnerList = [
        ...(r.partnersParsed || []),
        ...(r.partnersKPParsed || []),
      ];
      partnerList.forEach(p => {
        const type = p.deliveryType || p.type || 'Other';
        counts[type] = (counts[type] || 0) + 1;
      });
    });
    return Object.entries(counts)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);
  }, [filteredResults]);

  // ------------------------------------------------------------------
  // Aggregate totals
  // ------------------------------------------------------------------
  const totalResults = filteredResults.length;

  const totalCountries = useMemo(() => {
    const countrySet = new Set();
    filteredResults.forEach(r => {
      const countries = r.countriesParsed
        || (r.countries ? parseCountryList(r.countries) : []);
      countries.forEach(c => { if (c) countrySet.add(c); });
    });
    return countrySet.size;
  }, [filteredResults]);

  const totalPartners = useMemo(() => {
    const partnerSet = new Set();
    filteredResults.forEach(r => {
      const partnerList = [
        ...(r.partnersParsed || []),
        ...(r.partnersKPParsed || []),
      ];
      partnerList.forEach(p => {
        if (p.acronym) partnerSet.add(p.acronym);
      });
    });
    return partnerSet.size;
  }, [filteredResults]);

  const value = {
    // Raw data
    filteredResults,
    stats,

    // Breakdowns
    programBreakdown,
    impactAreaBreakdown,
    regionalBreakdown,
    typeBreakdown,
    fundingBreakdown,
    crossProgramResults,
    countryBreakdown,
    partnerBreakdown,

    // Totals
    totalResults,
    totalCountries,
    totalPartners,
  };

  return (
    <PortfolioDataContext.Provider value={value}>
      {children}
    </PortfolioDataContext.Provider>
  );
}

// ------------------------------------------------------------------
// Empty-shell fallback so charts degrade gracefully outside a provider
// ------------------------------------------------------------------
const EMPTY_CONTEXT = {
  filteredResults: [],
  stats: null,
  programBreakdown: [],
  impactAreaBreakdown: [],
  regionalBreakdown: [],
  typeBreakdown: [],
  fundingBreakdown: [],
  crossProgramResults: [],
  countryBreakdown: [],
  partnerBreakdown: [],
  totalResults: 0,
  totalCountries: 0,
  totalPartners: 0,
};

/**
 * usePortfolioData
 * Hook to read portfolio-wide aggregated data from context.
 * Returns an empty-shell object when called outside a provider so charts
 * that use the `dataProp ?? hookValue` pattern degrade gracefully.
 */
export function usePortfolioData() {
  const context = useContext(PortfolioDataContext);
  return context || EMPTY_CONTEXT;
}
